import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { PusherEvent } from '@mondaygpt/constants'
import { FacebookIcon, Tabs, TabsList, TabsTrigger, Textarea } from 'components'
import { useAnalytics } from 'hooks'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import {
  AlertTriangle,
  Building2,
  Camera,
  CheckCircle2,
  Globe,
  Instagram,
  Clock,
  Loader2,
  MessageCircle,
  Palette,
  Sparkles,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useIsMobile } from 'hooks/use-mobile'
import { MobilePageHeader } from '../components/MobilePageHeader'
import { TimezoneSelector } from '../../common/components/TimezoneSelector'
import { getBrowserTimezone } from '../../common/utils/business-timezone'
import { cleanUrl, extractSocialHandle, isValidUrl } from '../../common/utils/domain-validation'
import { checkFacebookPage } from '../api/brand-kit-api'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'
import { BrandImageLibrarySection } from '../components/BrandImageLibrarySection'
import { BRAND_TOOL_ACTIVITY_EVENT } from '../assistant-chat-events'
import { BrandMemoriesList, type BrandMemoriesListHandle } from '../components/BrandMemoriesList'
import { ColorDot, EditableText, LogoEditor } from '../components/BrandProfileFields'
import { useBrandKitApi } from '../hooks/useBrandKitApi'
import { useBrandKitStore } from '../store/brand-kit-store'
import type { BrandKit } from '../types'

// ─── Constants ──────────────────────────────────────────────────────────────

const AUTO_SAVE_DEBOUNCE_MS = 1000

const toneOptions: {
  value: BrandKit['toneOfVoice'][number]
  label: string
  description: string
}[] = [
  { value: 'professional', label: 'Professional', description: 'Polished and authoritative' },
  { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
  { value: 'playful', label: 'Playful', description: 'Fun and energetic' },
  { value: 'bold', label: 'Bold', description: 'Confident and energetic' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
]

// ─── Section Component ──────────────────────────────────────────────────────

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="card p-6">
      <div className="mb-5 flex items-center gap-2">
        <span className="text-acid">{icon}</span>
        <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">{title}</h3>
      </div>
      {children}
    </div>
  )
}

// ─── Brand Kit Page ─────────────────────────────────────────────────────────

export function BrandKitPage() {
  const { loading, savePartial, refetch } = useBrandKitApi()
  const { track } = useAnalytics()
  const isMobile = useIsMobile()
  const brandKit = useBrandKitStore(s => s.brandKit)
  const updateBrandKit = useBrandKitStore(s => s.updateBrandKit)

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hydratedRef = useRef(false)
  const lastSavedSnapshotRef = useRef<string>('')

  // ── Tabs ───────────────────────────────────────────────────────────────────
  // Profile (the brand fields) and Post Guidelines (the Brand Memory list). Both
  // panels stay mounted; we toggle visibility so counts, the new-guideline flash,
  // and the cross-tab change indicators survive a switch. activeTabRef mirrors the
  // state so the (stable) chat-activity handler reads the live tab without a
  // stale closure.
  // ?tab=guides deep-links straight to the guidelines (the chat's "Saved to
  // your brand rules" chip lands here — memory stays legible and editable).
  const initialTab: 'profile' | 'guides' =
    new URLSearchParams(window.location.search).get('tab') === 'guides' ? 'guides' : 'profile'
  const [activeTab, setActiveTab] = useState<'profile' | 'guides'>(initialTab)
  const activeTabRef = useRef<'profile' | 'guides'>(initialTab)
  const [profileDirty, setProfileDirty] = useState(false)
  // guidesDirty drives the count pill: it shows ONLY when a chat change touched
  // the guidelines while the user was on another tab, and clears when they open
  // the tab. It is not a persistent counter — no badge when nothing's unseen.
  const [guidesDirty, setGuidesDirty] = useState(false)
  const [guidelineCount, setGuidelineCount] = useState(0)
  const guidelineCountRef = useRef<number | null>(null)
  const brandMemoriesRef = useRef<BrandMemoriesListHandle>(null)

  const selectTab = useCallback((tab: 'profile' | 'guides') => {
    setActiveTab(tab)
    activeTabRef.current = tab
    if (tab === 'profile') setProfileDirty(false)
    if (tab === 'guides') setGuidesDirty(false)
  }, [])

  // React to ?tab= while ALREADY MOUNTED: the dock's "See it in Brand" chip
  // navigates to /brand?tab=guides, and with the dock living on this page the
  // user is often standing here — an initializer-only read made that click a
  // silent no-op.
  const location = useLocation()
  useEffect(() => {
    if (new URLSearchParams(location.search).get('tab') === 'guides') selectTab('guides')
  }, [location.search, selectTab])

  // Flag the Post Guidelines tab when the count changes while the user is on
  // another tab (e.g. the chat just captured a rule). The first report (prev ===
  // null) is the initial load and never flags.
  const handleGuidelineCount = useCallback((count: number) => {
    const prev = guidelineCountRef.current
    guidelineCountRef.current = count
    setGuidelineCount(count)
    if (prev !== null && count !== prev && activeTabRef.current !== 'guides') {
      setGuidesDirty(true)
    }
  }, [])

  // Facebook integration state
  const [fbConnected, setFbConnected] = useState<{ pageName: string } | null>(null)
  const [fbCheck, setFbCheck] = useState<'idle' | 'checking' | 'found' | 'not_found'>('idle')
  const lastCheckedFb = useRef('')
  const fbDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fbAbortRef = useRef<AbortController | null>(null)

  const refreshFbStatus = useCallback(() => {
    IntegrationsService.integrationControllerGetIntegrationStatusV1('facebook')
      .then(status => {
        if (status.isConnected && status.config?.pageName) {
          setFbConnected({ pageName: status.config.pageName })
        } else {
          setFbConnected(null)
        }
      })
      .catch(err => console.error('Failed to check Facebook integration status:', err))
  }, [])

  useEffect(() => {
    refreshFbStatus()
  }, [refreshFbStatus])

  // Refresh when the user connects/disconnects Facebook elsewhere (Composer,
  // IntegrationsPage). Server fires OAuthUpdated post-persistence so this is
  // the authoritative cross-surface signal.
  useListenToPusherEvent<{ provider: string }>(PusherEvent.OAuthUpdated, ({ provider }) => {
    if (provider?.toLowerCase() === 'facebook') refreshFbStatus()
  })

  const runFbCheck = useCallback(async (value: string) => {
    const handle = extractSocialHandle(value, 'facebook') || value.trim()
    if (!handle || handle === lastCheckedFb.current) return
    fbAbortRef.current?.abort()
    const controller = new AbortController()
    fbAbortRef.current = controller
    lastCheckedFb.current = handle
    setFbCheck('checking')
    try {
      const result = await checkFacebookPage(handle)
      if (controller.signal.aborted) return
      setFbCheck(result.exists && result.isPage ? 'found' : 'not_found')
    } catch {
      if (!controller.signal.aborted) {
        lastCheckedFb.current = ''
        setFbCheck('idle')
      }
    }
  }, [])

  const snapshotWithoutLogo = useCallback((bk: BrandKit) => {
    const { logoId: _, images: __, ...rest } = bk
    return JSON.stringify(rest)
  }, [])

  // Mark hydrated after initial load and capture the baseline snapshot
  useEffect(() => {
    if (!loading) {
      lastSavedSnapshotRef.current = snapshotWithoutLogo(brandKit)
      // Delay setting hydrated so the first render after load doesn't trigger auto-save
      requestAnimationFrame(() => {
        hydratedRef.current = true
      })
    }
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Track page view
  useEffect(() => {
    track(AnalyticsEvents.BrandKit.BRAND_KIT_VIEWED)
  }, [track])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (fbDebounceTimer.current) clearTimeout(fbDebounceTimer.current)
      fbAbortRef.current?.abort()
    }
  }, [])

  // Debounced auto-save effect
  useEffect(() => {
    if (!hydratedRef.current) return

    const currentSnapshot = snapshotWithoutLogo(brandKit)
    if (currentSnapshot === lastSavedSnapshotRef.current) return
    if (!brandKit.businessName.trim()) return
    if (brandKit.websiteUrl && !isValidUrl(brandKit.websiteUrl)) return

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

    debounceTimerRef.current = setTimeout(async () => {
      try {
        await savePartial(brandKit)
        lastSavedSnapshotRef.current = snapshotWithoutLogo(brandKit)
      } catch {
        // silent failure — next edit will retry
      }
    }, AUTO_SAVE_DEBOUNCE_MS)
  }, [brandKit, snapshotWithoutLogo, savePartial])

  // ─── Field helpers ─────────────────────────────────────────────────────────

  const trackFieldUpdated = useCallback(
    (fieldName: string) => {
      track(AnalyticsEvents.BrandKit.BRAND_KIT_FIELD_UPDATED, { field_name: fieldName })
    },
    [track]
  )

  const update = useCallback(
    <K extends keyof BrandKit>(field: K, value: BrandKit[K]) => {
      updateBrandKit({ [field]: value })
      const fieldName = String(field)
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
      trackFieldUpdated(fieldName)
    },
    [updateBrandKit, trackFieldUpdated]
  )

  const updateNested = useCallback(
    <K extends keyof BrandKit>(field: K, nestedField: string, value: string) => {
      const current = brandKit[field]
      if (typeof current === 'object' && current !== null) {
        updateBrandKit({ [field]: { ...current, [nestedField]: value } })

        if (String(field) === 'colors') {
          trackFieldUpdated(`${nestedField}_color`)
        } else if (String(field) === 'fonts') {
          trackFieldUpdated(`${nestedField}_font`)
        } else {
          trackFieldUpdated(nestedField)
        }
      }
    },
    [brandKit, updateBrandKit, trackFieldUpdated]
  )

  // The assistant chat (the dock on this page, or any other room) writes
  // brand-kit fields server-side. After any brand tool result, pull them back
  // so the page reflects the change, and reset the auto-save baseline so the
  // debounced save effect doesn't re-persist it.
  const handleChatToolActivity = useCallback(async () => {
    const before = snapshotWithoutLogo(useBrandKitStore.getState().brandKit)
    // The chat may have captured a guideline too. The memory list hits an
    // unrelated endpoint, so kick its refresh off first and let it run
    // concurrently with the brand-kit refetch instead of waiting behind it.
    brandMemoriesRef.current?.refresh()
    await refetch({ silent: true })
    const after = snapshotWithoutLogo(useBrandKitStore.getState().brandKit)
    lastSavedSnapshotRef.current = after
    // If a profile field actually changed while the user is looking at the
    // guidelines tab, flag the Profile tab so the change isn't invisible.
    if (before !== after && activeTabRef.current !== 'profile') {
      setProfileDirty(true)
    }
  }, [refetch, snapshotWithoutLogo])

  // Brand is a normal dock room: the assistant chat broadcasts brand-tool
  // activity as a window event (it renders in AppLayout, not here).
  useEffect(() => {
    window.addEventListener(BRAND_TOOL_ACTIVITY_EVENT, handleChatToolActivity)
    return () => window.removeEventListener(BRAND_TOOL_ACTIVITY_EVENT, handleChatToolActivity)
  }, [handleChatToolActivity])

  // Tab strip shared by the desktop sticky header and the mobile header.
  const tabBar = (
    <TabsList variant="pill">
      <TabsTrigger value="profile" variant="pill">
        Profile
        {profileDirty && (
          <span
            aria-label="Profile updated"
            className="ml-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-acid"
          />
        )}
      </TabsTrigger>
      <TabsTrigger value="guides" variant="pill">
        Post Guidelines
        {guidesDirty && (
          <span className="mono text-ink-on-acid ml-2 animate-pulse rounded-full bg-acid px-[7px] py-[2px] text-[10px] leading-none font-bold">
            {guidelineCount}
          </span>
        )}
      </TabsTrigger>
    </TabsList>
  )

  return (
    <div className="flex h-full">
      {/* The Brand Kit fills the full width on its own; when the chat opens it
          docks to the RIGHT and the Brand Kit shrinks to fill the rest. */}
      <Tabs
        value={activeTab}
        onValueChange={v => selectTab(v as 'profile' | 'guides')}
        className="flex h-full min-w-0 flex-1 flex-col overflow-y-auto"
      >
        {/* ── Header ────────────────────────────────────────────────────── */}
        {isMobile ? (
          <>
            <MobilePageHeader title="My Brand" subtitle="Define your brand identity" />
            <div className="border-b border-line px-4 pb-3">{tabBar}</div>
          </>
        ) : (
          <div className="sticky top-0 z-10 border-b border-line bg-bg/90 backdrop-blur">
            <div className="flex w-full flex-col gap-3 px-6 pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
              <div>
                <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                  Brand
                </span>
                <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[26px]">
                  My Brand
                </h1>
                <p className="mt-1 text-[13px] text-paper-dim">
                  Define your brand identity. AI uses this to create on-brand content.
                </p>
              </div>
            </div>
            <div className="px-6 pt-3 pb-3">{tabBar}</div>
          </div>
        )}

        {/* ── Content ──────────────────────────────────────────────────────── */}
        <div className="w-full px-6 pt-6 pb-12">
          {loading ? (
            <div className="card flex min-h-[200px] items-center justify-center">
              <Loader2 size={24} className="animate-spin text-acid" />
            </div>
          ) : (
            <>
              {/* ── Profile panel ──────────────────────────────────────────── */}
              <div className={`space-y-6 ${activeTab !== 'profile' ? 'hidden' : ''}`}>
                {/* Identity banner: logo + name + industry, edited inline. */}
                <div className="card-bright p-6">
                  <div className="flex items-center gap-5">
                    <LogoEditor
                      logo={brandKit.logoId || brandKit.instagramProfilePictureId}
                      name={brandKit.businessName}
                      onUploaded={() => {
                        trackFieldUpdated('logo')
                        track(AnalyticsEvents.BrandKit.BRAND_KIT_LOGO_UPLOADED, {})
                      }}
                      onRemoved={() => {
                        trackFieldUpdated('logo')
                        track(AnalyticsEvents.BrandKit.BRAND_KIT_LOGO_REMOVED, {})
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <EditableText
                        value={brandKit.businessName}
                        placeholder="Business name"
                        onSave={v => update('businessName', v)}
                        className="t-h2"
                        underline={false}
                      />
                      {!brandKit.businessName.trim() && (
                        <p className="t-body-sm mt-0.5 text-[color:var(--alert-error-text)]">
                          Business name is required
                        </p>
                      )}
                      <EditableText
                        value={brandKit.industry}
                        placeholder="Your industry"
                        onSave={v => update('industry', v)}
                        // Was `truncate`: whitespace-nowrap stops the field from
                        // wrapping, and an always-live field has no separate
                        // display node to clip. Cap the height instead, so a long
                        // industry wraps to a second line and then scrolls rather
                        // than pushing the identity card open.
                        className="t-body-sm mt-1"
                        maxLines={2}
                        dimmed
                        underline={false}
                        allowEmpty
                      />
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <Section icon={<Building2 size={18} />} title="Business Info">
                  <div className="space-y-4">
                    <div>
                      <label className="mono mb-1.5 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Business Name{' '}
                        <span className="text-[color:var(--alert-error-text)]">*</span>
                      </label>
                      <input
                        type="text"
                        value={brandKit.businessName}
                        onChange={e => update('businessName', e.target.value)}
                        placeholder="Your business name"
                        required
                        className={`w-full rounded-[9px] border bg-bg-3 px-3 py-2 text-[13.5px] text-paper transition-all outline-none placeholder:text-paper-faint focus:border-primary! ${!brandKit.businessName.trim() ? 'border-[color:var(--alert-error-border)]' : 'border-line-bright'}`}
                      />
                      {!brandKit.businessName.trim() && (
                        <p className="mt-1 text-[12px] text-[color:var(--alert-error-text)]">
                          Business name is required
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mono mb-1.5 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Industry
                      </label>
                      <Textarea
                        variant="dark"
                        value={brandKit.industry}
                        onChange={e => update('industry', e.target.value)}
                        placeholder="e.g., Bakery, Consulting"
                        rows={2}
                        className="resize-none text-[13.5px]"
                      />
                    </div>
                    <div>
                      <label className="mono mb-1.5 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Target Audience
                      </label>
                      <Textarea
                        variant="dark"
                        value={brandKit.targetAudience}
                        onChange={e => update('targetAudience', e.target.value)}
                        placeholder="e.g., Young professionals"
                        rows={2}
                        className="resize-none text-[13.5px]"
                      />
                    </div>
                    <div>
                      <label className="mono mb-1.5 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Business Description
                      </label>
                      <Textarea
                        variant="dark"
                        value={brandKit.description}
                        onChange={e => update('description', e.target.value)}
                        placeholder="What does your business do? Who do you serve?"
                        rows={3}
                        className="resize-none text-[13.5px]"
                      />
                    </div>
                  </div>
                </Section>

                {/* Scheduling — anchors every scheduled post, autopilot slot, and
                calendar bucketing to a single zone. Null is allowed
                pre-capture; once set, the value flows everywhere via
                BrandKit.timezone. */}
                <Section icon={<Clock size={18} />} title="Scheduling">
                  <div className="space-y-2">
                    <label className="mono block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                      Timezone
                    </label>
                    <TimezoneSelector
                      value={brandKit.timezone ?? getBrowserTimezone()}
                      onChange={tz => update('timezone', tz)}
                    />
                    <p className="text-[12px] text-paper-faint">
                      All scheduled posts and autopilot slots fire at the chosen wall-clock time in
                      this zone.
                    </p>
                  </div>
                </Section>

                {/* Brand Image Library */}
                <Section icon={<Camera size={18} />} title="Brand Image Library">
                  <BrandImageLibrarySection />
                </Section>

                {/* Visual Identity */}
                <Section icon={<Palette size={18} />} title="Visual Identity">
                  <div className="space-y-5">
                    <div>
                      <label className="mono mb-2 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Brand Colors
                      </label>
                      <div className="flex flex-wrap items-center gap-4">
                        {(['primary', 'secondary', 'accent'] as const).map(k => (
                          <span key={k} className="inline-flex items-center gap-2">
                            <ColorDot
                              hex={brandKit.colors[k]}
                              onChange={next => updateNested('colors', k, next)}
                            />
                            <span className="mono text-[12px] text-paper-faint uppercase">
                              {brandKit.colors[k].replace(/^#/, '')}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mono mb-2 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                        Fonts
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {(
                          [
                            {
                              key: 'heading',
                              label: 'Heading',
                              placeholder: 'e.g., Inter, Playfair Display',
                            },
                            { key: 'body', label: 'Body', placeholder: 'e.g., Inter, Open Sans' },
                          ] as const
                        ).map(({ key, label, placeholder }) => (
                          <div key={key}>
                            <label className="mono mb-1 block text-[10px] tracking-[0.18em] text-paper-faint uppercase">
                              {label}
                            </label>
                            <input
                              type="text"
                              value={brandKit.fonts[key]}
                              onChange={e => updateNested('fonts', key, e.target.value)}
                              placeholder={placeholder}
                              className="w-full rounded-[9px] border border-line-bright bg-bg-3 px-3 py-2 text-[13.5px] text-paper transition-all outline-none placeholder:text-paper-faint focus:border-primary!"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Section>

                {/* Brand Voice */}
                <Section icon={<MessageCircle size={18} />} title="Brand Voice">
                  <div>
                    <label className="mono mb-2 block text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                      Tone of Voice
                    </label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {toneOptions.map(opt => {
                        const selected = brandKit.toneOfVoice?.includes(opt.value)
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              const current = brandKit.toneOfVoice || []
                              const next = current.includes(opt.value)
                                ? current.filter(t => t !== opt.value)
                                : [...current, opt.value]
                              updateBrandKit({ toneOfVoice: next.length > 0 ? next : current })
                              trackFieldUpdated('tone_of_voice')
                            }}
                            className={`rounded-[10px] border px-3 py-2.5 text-left transition-colors ${
                              selected
                                ? 'border-acid bg-[color:var(--acid-tint)] shadow-[0_0_0_3px_var(--acid-soft)]'
                                : 'border-line bg-bg-3 hover:border-line-bright'
                            }`}
                          >
                            <div
                              className={`text-[12px] font-medium ${
                                selected ? 'text-acid' : 'text-paper'
                              }`}
                            >
                              {opt.label}
                            </div>
                            <div className="mt-0.5 text-[11px] text-paper-faint">
                              {opt.description}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </Section>

                {/* Channels */}
                <Section icon={<Globe size={18} />} title="Channels">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-5 flex-shrink-0 text-paper-faint">
                        <Globe size={14} />
                      </span>
                      <input
                        type="text"
                        value={brandKit.websiteUrl || ''}
                        onChange={e => update('websiteUrl', e.target.value || null)}
                        onBlur={() => {
                          const raw = brandKit.websiteUrl?.trim()
                          if (!raw) return
                          const cleaned = cleanUrl(raw)
                          if (cleaned !== raw) {
                            updateBrandKit({ websiteUrl: cleaned })
                          }
                        }}
                        placeholder="yoursite.com"
                        className={`w-full rounded-[9px] border bg-bg-3 px-3 py-2 text-[13.5px] text-paper transition-all outline-none placeholder:text-paper-faint focus:border-primary! ${brandKit.websiteUrl && !isValidUrl(brandKit.websiteUrl) ? 'border-[color:var(--alert-error-border)]' : 'border-line-bright'}`}
                      />
                      {brandKit.websiteUrl && !isValidUrl(brandKit.websiteUrl) && (
                        <p className="mt-1 text-[12px] text-[color:var(--alert-error-text)]">
                          Please enter a valid URL (e.g., yoursite.com)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 flex-shrink-0 text-paper-faint">
                        <Instagram size={14} />
                      </span>
                      <input
                        type="text"
                        value={brandKit.channels?.instagram?.handle || ''}
                        onChange={e => {
                          const channels = {
                            ...brandKit.channels,
                            instagram: { ...brandKit.channels?.instagram, handle: e.target.value },
                          }
                          updateBrandKit({ channels })
                          trackFieldUpdated('instagram_handle')
                        }}
                        onBlur={() => {
                          const current = brandKit.channels?.instagram?.handle || ''
                          const extracted = extractSocialHandle(current, 'instagram')
                          if (extracted && extracted !== current) {
                            const channels = {
                              ...brandKit.channels,
                              instagram: { ...brandKit.channels?.instagram, handle: extracted },
                            }
                            updateBrandKit({ channels })
                          }
                        }}
                        placeholder="@yourbusiness"
                        className="w-full rounded-[9px] border border-line-bright bg-bg-3 px-3 py-2 text-[13.5px] text-paper transition-all outline-none placeholder:text-paper-faint focus:border-primary!"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="w-5 flex-shrink-0 text-paper-faint">
                          <FacebookIcon size={14} />
                        </span>
                        {fbConnected ? (
                          <div className="flex w-full items-center gap-2 rounded-[9px] border border-line bg-bg-3 px-3 py-2">
                            <span className="text-[13.5px] text-paper">{fbConnected.pageName}</span>
                            <span className="text-success mono inline-flex items-center rounded-full bg-[color:var(--success-tint)] px-2 py-0.5 text-[10px] font-medium">
                              Connected
                            </span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={brandKit.channels?.facebook?.handle || ''}
                            onChange={e => {
                              const channels = {
                                ...brandKit.channels,
                                facebook: {
                                  ...brandKit.channels?.facebook,
                                  handle: e.target.value,
                                },
                              }
                              updateBrandKit({ channels })
                              trackFieldUpdated('facebook_handle')
                              if (fbCheck !== 'idle' && fbCheck !== 'checking') setFbCheck('idle')
                            }}
                            onBlur={() => {
                              const current = brandKit.channels?.facebook?.handle || ''
                              const extracted = extractSocialHandle(current, 'facebook')
                              if (extracted && extracted !== current) {
                                const channels = {
                                  ...brandKit.channels,
                                  facebook: { ...brandKit.channels?.facebook, handle: extracted },
                                }
                                updateBrandKit({ channels })
                              }
                              fbDebounceTimer.current && clearTimeout(fbDebounceTimer.current)
                              if (current.trim()) runFbCheck(current)
                            }}
                            placeholder="Facebook Page name or URL"
                            className="w-full rounded-[9px] border border-line-bright bg-bg-3 px-3 py-2 text-[13.5px] text-paper transition-all outline-none placeholder:text-paper-faint focus:border-primary!"
                          />
                        )}
                      </div>
                      {!fbConnected && fbCheck === 'checking' && (
                        <p className="mt-1 flex items-center gap-1.5 pl-8 text-[12px] text-paper-faint">
                          <Loader2 size={12} className="animate-spin" />
                          Checking your Facebook Page...
                        </p>
                      )}
                      {!fbConnected && fbCheck === 'found' && (
                        <p className="mt-1 flex items-center gap-1.5 pl-8 text-xs text-emerald-600">
                          <CheckCircle2 size={12} />
                          Page found!
                        </p>
                      )}
                      {!fbConnected && fbCheck === 'not_found' && (
                        <p className="mt-1 flex items-center gap-1.5 pl-8 text-[12px] text-[color:var(--warning)]">
                          <AlertTriangle size={12} />
                          We couldn't find this Facebook Page. Make sure it's a Page, not a personal
                          profile.
                        </p>
                      )}
                    </div>
                  </div>
                </Section>
              </div>
              {/* ── end Profile panel ──────────────────────────────────────── */}

              {/* ── Post Guidelines panel ──────────────────────────────────── */}
              <div className={activeTab !== 'guides' ? 'hidden' : ''}>
                <div className="card p-6">
                  <div className="mb-1.5 flex items-center gap-2">
                    <Sparkles size={18} className="text-acid" />
                    <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">
                      Post Guidelines
                    </h3>
                  </div>
                  <p className="mb-5 text-[13px] leading-relaxed text-paper-dim">
                    The rules Taka follows in every post it writes for you. Add one here, or just
                    tell Taka in chat.
                  </p>
                  <BrandMemoriesList
                    ref={brandMemoriesRef}
                    appearance="card"
                    onCountChange={handleGuidelineCount}
                    emptyText="No guidelines yet. Add one below, or tell Taka in chat."
                  />
                </div>
              </div>
              {/* ── end Post Guidelines panel ──────────────────────────────── */}
            </>
          )}
        </div>
      </Tabs>
    </div>
  )
}
